import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CircleUserRound, FolderKanban, House, Image, LogIn, Menu, MessageSquare, MoonStar, SunMedium, X } from 'lucide-react';
import { SiteLogo } from '../ui/SiteLogo';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { useOpenSettings } from '../../features/settings/SettingsModalContext';
import { selectDirection, toggleTheme } from '../../redux/slices/appSlice';
import { classNames } from '../../utils/classNames';
import { Button } from '../ui/Button';
import { WalletBalanceBadge } from './WalletBalanceBadge';
import { AppSidebar } from './AppSidebar';
import { HeaderMobileMenu, mobileMenuItemClass } from './HeaderMobileMenu';
import { UpgradePlanButton } from './UpgradePlanButton';
import { useMyPackage } from '../../hooks/useMyPackage';

const UPGRADE_HEADER_PATHS = /^\/(home|chat|projects|images|cloud|apps)(\/|$)/;

const NavItem = ({ to, label, icon, disabled = false, className = '', mobile = false }) => (
  <NavLink
    to={disabled ? '#' : to}
    onClick={(event) => {
      if (disabled) {
        event.preventDefault();
      }
    }}
    className={({ isActive }) =>
      classNames(
        mobile ? mobileMenuItemClass(isActive && !disabled) : 'nav-link group',
        !mobile && isActive && !disabled ? 'nav-link-active' : '',
        !mobile && disabled ? 'cursor-not-allowed opacity-45' : '',
        mobile && disabled ? 'pointer-events-none opacity-45' : '',
        className
      )
    }
  >
    <span className={classNames('inline-flex items-center gap-3 text-sm font-medium', mobile ? 'w-full' : 'justify-center gap-2')}>
      {icon ? <span className={mobile ? 'shrink-0' : 'nav-link-icon'}>{icon}</span> : null}
      {label}
    </span>
  </NavLink>
);

const SIDEBAR_COLLAPSED_KEY = 'risman.sidebar.collapsed';
const WORKSPACE_SIDEBAR_COLLAPSED_KEY = 'risman.sidebar.workspace.collapsed';

const MobileSidebarDrawer = ({ open, onClose }) => {
  const location = useLocation();
  const { t } = useI18n();
  const direction = useAppSelector(selectDirection);
  const slideOffset = direction === 'rtl' ? '100%' : '-100%';

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <AnimatePresence>
      {open ? (
        <div key="mobile-sidebar-drawer" className="fixed inset-0 z-[80] md:hidden">
          <motion.button
            type="button"
            className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]"
            onClick={onClose}
            aria-label={t('close')}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            className="absolute inset-y-0 start-0 flex h-full w-[min(20.5rem,calc(100vw-2.5rem))] overflow-hidden bg-app shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label={t('navigation')}
            initial={{ x: slideOffset }}
            animate={{ x: 0 }}
            exit={{ x: slideOffset }}
            transition={{ type: 'spring', stiffness: 380, damping: 36 }}
          >
            <AppSidebar variant="drawer" onRequestClose={onClose} />
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
};

const MobileAppBar = ({ open, onOpen, onClose, trailing = null }) => {
  const { t } = useI18n();

  return (
    <div className="flex h-12 shrink-0 items-center gap-1 border-b border-border px-2 md:hidden">
      <button
        type="button"
        onClick={open ? onClose : onOpen}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-text transition hover:bg-accent-soft"
        aria-label={open ? t('close') : t('openMenu')}
        aria-expanded={open}
      >
        {open ? <X size={20} /> : <Menu size={20} />}
      </button>
      <Link to="/home" className="flex min-w-0 flex-1 items-center gap-2 px-1">
        <SiteLogo size="sm" className="shadow-none" />
        <span className="truncate text-sm font-semibold">{t('appName')}</span>
      </Link>
      {trailing}
    </div>
  );
};

export const AppShell = ({
  title,
  children,
  breadcrumbActions = null,
  breadcrumbStartActions = null,
  breadcrumbNotice = null,
  variant = 'dashboard',
  workspaceHeader = null,
  hideSidebar = false
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const openSettings = useOpenSettings();
  const dispatch = useAppDispatch();
  const { t } = useI18n();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isWorkspace = variant === 'workspace';
  const isClassic = variant === 'classic';
  const sidebarStorageKey = isWorkspace ? WORKSPACE_SIDEBAR_COLLAPSED_KEY : SIDEBAR_COLLAPSED_KEY;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = window.localStorage.getItem(
        variant === 'workspace' ? WORKSPACE_SIDEBAR_COLLAPSED_KEY : SIDEBAR_COLLAPSED_KEY
      );
      if (stored === null) {
        return variant === 'workspace';
      }
      return stored === '1';
    } catch {
      return variant === 'workspace';
    }
  });
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const theme = useAppSelector((state) => state.app.theme);
  const entitlement = useMyPackage();
  const showUpgradeButton =
    isAuthenticated &&
    Boolean(entitlement) &&
    !entitlement.hasPackage &&
    UPGRADE_HEADER_PATHS.test(location.pathname);

  useEffect(() => {
    try {
      window.localStorage.setItem(sidebarStorageKey, sidebarCollapsed ? '1' : '0');
    } catch {
      // ignore
    }
  }, [sidebarCollapsed, sidebarStorageKey]);

  const navItems = [
    {
      to: '/home',
      label: t('home'),
      icon: <House size={16} />
    },
    {
      to: '/chat',
      label: t('chatSection'),
      icon: <MessageSquare size={16} />
    },
    {
      to: '/projects',
      label: t('productGeneration'),
      icon: <FolderKanban size={16} />
    },
    {
      to: '/images',
      label: t('imageSection'),
      icon: <Image size={16} />
    }
  ];

  if (isWorkspace) {
    return (
      <div className="flex h-[100dvh] min-h-0 overflow-hidden bg-app text-text">
        <AppSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
        />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <MobileAppBar open={mobileNavOpen} onOpen={openMobileNav} onClose={closeMobileNav} />
          <MobileSidebarDrawer open={mobileNavOpen} onClose={closeMobileNav} />
          {typeof workspaceHeader === 'function'
            ? workspaceHeader({
                sidebarCollapsed,
                onToggleSidebar: () => setSidebarCollapsed((value) => !value)
              })
            : workspaceHeader}
          {breadcrumbNotice}
          <main className="min-h-0 min-w-0 flex-1 overflow-hidden">{children}</main>
        </div>
      </div>
    );
  }

  if (isClassic || hideSidebar) {
    return (
      <div className="relative isolate min-h-screen overflow-hidden bg-white px-3 py-3 sm:px-4 lg:px-6 dark:bg-panel-strong">
        <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 overflow-visible">
          <header className="rounded-2xl border border-border bg-white px-4 py-3 dark:border-border dark:bg-panel-strong md:px-5">
            <div className="flex items-center justify-between gap-3">
              <Link to="/" className="flex min-w-0 items-center gap-3 transition hover:opacity-90" aria-label={t('navHome')}>
                <SiteLogo size="md" className="shadow-none" />
                <span className="text-base font-bold tracking-tight text-text md:text-lg">{t('appName')}</span>
              </Link>

              <nav className="hidden items-center gap-1 lg:flex lg:gap-2">
                {navItems.map((item) => (
                  <NavItem key={item.label} {...item} />
                ))}
              </nav>

              <div className="flex shrink-0 items-center justify-end gap-2">
                <HeaderMobileMenu
                  open={mobileNavOpen}
                  onOpen={openMobileNav}
                  onClose={closeMobileNav}
                  brandTitle={t('appName')}
                  brandSubtitle={t('landingTopTag')}
                  panelClassName="bg-panel-strong"
                  footer={
                    <>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          dispatch(toggleTheme());
                        }}
                        className="relative inline-flex w-full items-center gap-3 rounded-xl border border-border px-3.5 py-3 text-sm font-semibold transition hover:bg-accent-soft/60 dark:border-border dark:hover:bg-accent-soft"
                      >
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-accent-soft text-text">
                          {theme === 'light' ? <MoonStar size={18} /> : <SunMedium size={18} />}
                        </span>
                        <span>{theme === 'light' ? t('dark') : t('light')}</span>
                      </button>

                      {isAuthenticated ? <WalletBalanceBadge className="w-full" expanded /> : null}

                      {isAuthenticated ? (
                        <Button
                          variant="ghost"
                          className="w-full justify-start gap-3 rounded-xl border border-border px-3.5 py-3 dark:border-border"
                          onClick={() => openSettings()}
                        >
                          <CircleUserRound size={18} aria-hidden />
                          {t('settings')}
                        </Button>
                      ) : (
                        <Button className="w-full justify-start gap-3 rounded-xl px-3.5 py-3" onClick={() => navigate('/login')}>
                          <LogIn size={18} />
                          {t('login')}
                        </Button>
                      )}
                    </>
                  }
                >
                  {navItems.map((item) => (
                    <NavItem key={item.label} {...item} mobile />
                  ))}
                </HeaderMobileMenu>

                <div className="hidden items-center gap-2 lg:flex">
                  <button
                    type="button"
                    onClick={() => dispatch(toggleTheme())}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border text-text transition hover:bg-accent-soft"
                    title={theme === 'light' ? t('dark') : t('light')}
                    aria-label={theme === 'light' ? t('dark') : t('light')}
                  >
                    {theme === 'light' ? <MoonStar size={18} /> : <SunMedium size={18} />}
                  </button>

                  {isAuthenticated ? <WalletBalanceBadge /> : null}

                  {isAuthenticated ? (
                    <Button variant="ghost" className="border border-border" onClick={() => openSettings()}>
                      <CircleUserRound size={18} aria-hidden />
                      {t('settings')}
                    </Button>
                  ) : (
                    <Button onClick={() => navigate('/login')}>
                      <LogIn size={18} />
                      {t('login')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </header>

          {(title || breadcrumbActions) && (
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-3 px-1">
              {title ? <h1 className="text-lg font-semibold tracking-tight text-text">{title}</h1> : <div />}
              {breadcrumbActions ? <div className="flex flex-wrap items-center gap-2">{breadcrumbActions}</div> : null}
            </div>
          )}

          {breadcrumbNotice}
          <main className="min-w-0">{children}</main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] min-h-0 overflow-hidden bg-app text-text">
      <div className="hidden md:contents">
        <AppSidebar
          collapsed={sidebarCollapsed}
          onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <MobileAppBar
          open={mobileNavOpen}
          onOpen={openMobileNav}
          onClose={closeMobileNav}
          trailing={showUpgradeButton ? <UpgradePlanButton compact /> : null}
        />
        <MobileSidebarDrawer open={mobileNavOpen} onClose={closeMobileNav} />

        {(breadcrumbActions || breadcrumbStartActions || title) &&
        (breadcrumbActions || breadcrumbStartActions || title) ? (
          <div className="hidden h-12 shrink-0 items-center justify-between gap-3 border-b border-border px-4 dark:border-border sm:px-6 md:flex">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {breadcrumbStartActions ? (
                <div className="flex shrink-0 flex-wrap items-center gap-2">{breadcrumbStartActions}</div>
              ) : null}
              {title ? (
                <h1 className="truncate text-sm font-semibold text-text">{title}</h1>
              ) : null}
            </div>
            {breadcrumbActions || showUpgradeButton ? (
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {showUpgradeButton ? <UpgradePlanButton /> : null}
                {breadcrumbActions}
              </div>
            ) : null}
          </div>
        ) : null}

        {breadcrumbActions || breadcrumbStartActions || title ? (
          <div className="flex min-h-11 shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-1.5 md:hidden">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {breadcrumbStartActions}
              {title ? <h1 className="truncate text-sm font-semibold text-text">{title}</h1> : null}
            </div>
            {breadcrumbActions ? <div className="flex shrink-0 items-center gap-2">{breadcrumbActions}</div> : null}
          </div>
        ) : null}

        {breadcrumbNotice}
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
};

